import * as React from "react";

import { api } from "@repo/backend";
import { ROLES, Role, changePasswordSchema, createUserSchema } from "@repo/shared/auth/users";
import {
  ActionBar,
  ActionBarClose,
  ActionBarGroup,
  ActionBarItem,
  ActionBarSelection,
  ActionBarSeparator,
} from "@repo/ui/components/action-bar";
import { Avatar, AvatarFallback, AvatarImage } from "@repo/ui/components/avatar";
import { Button } from "@repo/ui/components/button";
import { Checkbox } from "@repo/ui/components/checkbox";
import { useConfirmationDialog } from "@repo/ui/components/confirmation-dialog-provider";
import { DataTableColumnHeader } from "@repo/ui/components/data-table/data-table-column-header";
import { DataTableToolbar } from "@repo/ui/components/data-table/data-table-toolbar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@repo/ui/components/dropdown-menu";
import { Field, FieldError, FieldGroup, FieldLabel } from "@repo/ui/components/field";
import { Input } from "@repo/ui/components/input";
import { RelativeTimeCard } from "@repo/ui/components/relative-time-card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@repo/ui/components/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@repo/ui/components/sheet";
import { useDataTable } from "@repo/ui/hooks/use-data-table";
import { getInitials } from "@repo/ui/lib/utils";
import { useForm } from "@tanstack/react-form";
import { createFileRoute } from "@tanstack/react-router";
import { type StockFeatures, ColumnDef, Table } from "@tanstack/react-table";
import {
  optimisticallyUpdateValueInPaginatedQuery,
  useMutation,
  usePaginatedQuery,
} from "convex/react";
import { ConvexError } from "convex/values";
import { EllipsisIcon, Loader2Icon, LockIcon, PlusIcon, ShieldUserIcon, XIcon } from "lucide-react";
import { toast } from "sonner";

import { DataTable } from "@/components/data-table";

export const Route = createFileRoute("/_authed/users/manage")({
  head: () => ({ meta: [{ title: "Manage users" }] }),
  component: RouteComponent,
});

type User = (typeof api.admin.listUsers)["_returnType"]["page"][number];

function TableActionBar({ table }: { table: Table<StockFeatures, User> }) {
  const confirm = useConfirmationDialog();

  const rows = table.getFilteredSelectedRowModel().rows;
  const onOpenChange = React.useCallback(
    (open: boolean) => {
      if (!open) {
        table.toggleAllRowsSelected(false);
      }
    },
    [table],
  );

  const handleDeactivate = React.useCallback(async (e: Event) => {
    e.preventDefault();

    const confirmed = await confirm({
      title: "Deactivate User",
      description: (
        <>
          Are you sure you want to deactivate this user? They will not be able to log in or access
          any resources.
        </>
      ),
      confirmText: "Deactivate",
      confirmButtonVariant: "destructive",
    });

    if (!confirmed) {
      return;
    }
  }, []);

  return (
    <ActionBar open={rows.length > 0} onOpenChange={onOpenChange}>
      <ActionBarSelection>
        {rows.length} {rows.length === 1 ? "User" : "Users"} selected
        <ActionBarClose>
          <XIcon />
        </ActionBarClose>
      </ActionBarSelection>
      <ActionBarSeparator />
      <ActionBarGroup>
        <ActionBarItem variant="secondary">
          <ShieldUserIcon /> Change Role
        </ActionBarItem>
        <ActionBarItem variant="destructive" onSelect={handleDeactivate}>
          <LockIcon /> Deactivate
        </ActionBarItem>
      </ActionBarGroup>
    </ActionBar>
  );
}

function CreateUserSheet() {
  const createUser = useMutation(api.admin.createUser);
  const [open, setOpen] = React.useState(false);

  const form = useForm({
    defaultValues: {
      name: "",
      email: "",
      password: "",
      role: "demo" as Role,
    },
    validators: {
      onChange: createUserSchema,
    },
    onSubmit: async ({ value }) => {
      try {
        await createUser({
          name: value.name.trim(),
          email: value.email.trim().toLowerCase(),
          password: value.password,
          role: value.role,
        });

        form.reset();
        setOpen(false);
        toast.success("User account created.");
      } catch (error) {
        const message = error instanceof Error ? error.message : "Failed to create user.";
        toast.error(message);
      }
    },
  });

  return (
    <Sheet
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (nextOpen) {
          form.reset();
        }
      }}
    >
      <SheetTrigger
        render={(props) => (
          <Button {...props} size="sm">
            <PlusIcon />
            Create user
          </Button>
        )}
      >
        Open
      </SheetTrigger>
      <SheetContent className="data-[side=right]:w-full data-[side=right]:sm:max-w-xl">
        <form
          className="flex h-full flex-col"
          onSubmit={(event) => {
            event.preventDefault();
            event.stopPropagation();
            void form.handleSubmit();
          }}
        >
          <SheetHeader>
            <SheetTitle>Create password account</SheetTitle>
            <SheetDescription>
              Create an internal password-based account for demos, QA, or review.
            </SheetDescription>
          </SheetHeader>

          <div className="flex flex-1 flex-col justify-between">
            <div className="grid gap-5 px-4">
              <FieldGroup>
                <form.Field name="name">
                  {(field) => {
                    const isInvalid = field.state.meta.isTouched && !field.state.meta.isValid;

                    return (
                      <Field data-invalid={isInvalid}>
                        <FieldLabel htmlFor="create-user-name">Name</FieldLabel>
                        <Input
                          id="create-user-name"
                          value={field.state.value}
                          onBlur={field.handleBlur}
                          onChange={(event) => field.handleChange(event.target.value)}
                          placeholder="WorkGrid Demo"
                          required
                        />
                        {isInvalid ? <FieldError errors={field.state.meta.errors} /> : null}
                      </Field>
                    );
                  }}
                </form.Field>

                <form.Field name="email">
                  {(field) => {
                    const isInvalid = field.state.meta.isTouched && !field.state.meta.isValid;

                    return (
                      <Field data-invalid={isInvalid}>
                        <FieldLabel htmlFor="create-user-email">Email</FieldLabel>
                        <Input
                          id="create-user-email"
                          type="email"
                          value={field.state.value}
                          onBlur={field.handleBlur}
                          onChange={(event) => field.handleChange(event.target.value)}
                          placeholder="demo-user@workgrid.local"
                          required
                        />
                        {isInvalid ? <FieldError errors={field.state.meta.errors} /> : null}
                      </Field>
                    );
                  }}
                </form.Field>

                <form.Field name="password">
                  {(field) => {
                    const isInvalid = field.state.meta.isTouched && !field.state.meta.isValid;

                    return (
                      <Field data-invalid={isInvalid}>
                        <FieldLabel htmlFor="create-user-password">Password</FieldLabel>
                        <Input
                          id="create-user-password"
                          type="password"
                          value={field.state.value}
                          onBlur={field.handleBlur}
                          onChange={(event) => field.handleChange(event.target.value)}
                          placeholder="At least 8 characters"
                          required
                        />
                        {isInvalid ? <FieldError errors={field.state.meta.errors} /> : null}
                      </Field>
                    );
                  }}
                </form.Field>

                <form.Field name="role">
                  {(field) => {
                    const isInvalid = field.state.meta.isTouched && !field.state.meta.isValid;

                    return (
                      <Field data-invalid={isInvalid}>
                        <FieldLabel htmlFor="create-user-role">Role</FieldLabel>
                        <Select
                          value={field.state.value}
                          onValueChange={(value) => value && field.handleChange(value)}
                        >
                          <SelectTrigger id="create-user-role" onBlur={field.handleBlur}>
                            <SelectValue placeholder="Select role" />
                          </SelectTrigger>
                          <SelectContent>
                            {ROLES.map((itemRole) => (
                              <SelectItem key={itemRole} value={itemRole} className="capitalize">
                                {itemRole}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {isInvalid ? <FieldError errors={field.state.meta.errors} /> : null}
                      </Field>
                    );
                  }}
                </form.Field>
              </FieldGroup>
            </div>

            <div className="border-t px-4 pt-4">
              <form.Subscribe selector={(state) => [state.canSubmit, state.isSubmitting]}>
                {([canSubmit, isSubmitting]) => (
                  <Button className="w-full" type="submit" disabled={!canSubmit || isSubmitting}>
                    {isSubmitting ? <Loader2Icon className="size-4 animate-spin" /> : null}
                    Create user
                  </Button>
                )}
              </form.Subscribe>
            </div>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
}

function ChangePasswordSheet({
  userId,
  userName,
  open,
  onOpenChange,
}: {
  userId: string;
  userName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const changePassword = useMutation(api.admin.changeUserPassword);

  const form = useForm({
    defaultValues: {
      password: "",
    },
    validators: {
      onChange: changePasswordSchema,
    },
    onSubmit: async ({ value }) => {
      try {
        await changePassword({ userId, password: value.password });
        form.reset();
        onOpenChange(false);
        toast.success("Password changed successfully.");
      } catch (error) {
        if (error instanceof ConvexError) {
          toast.error(error.data);
        } else {
          toast.error("Failed to change password.");
        }
      }
    },
  });

  return (
    <Sheet
      open={open}
      onOpenChange={(nextOpen) => {
        onOpenChange(nextOpen);
        if (nextOpen) form.reset();
      }}
    >
      <SheetContent className="data-[side=right]:w-full data-[side=right]:sm:max-w-md">
        <form
          className="flex h-full flex-col"
          onSubmit={(event) => {
            event.preventDefault();
            event.stopPropagation();
            void form.handleSubmit();
          }}
        >
          <SheetHeader>
            <SheetTitle>Change password</SheetTitle>
            <SheetDescription>
              Set a new password for <strong>{userName}</strong>.
            </SheetDescription>
          </SheetHeader>

          <div className="flex flex-1 flex-col justify-between">
            <div className="grid gap-5 px-4">
              <form.Field name="password">
                {(field) => {
                  const isInvalid = field.state.meta.isTouched && !field.state.meta.isValid;

                  return (
                    <Field data-invalid={isInvalid}>
                      <FieldLabel htmlFor="change-password">New password</FieldLabel>
                      <Input
                        id="change-password"
                        type="password"
                        value={field.state.value}
                        onBlur={field.handleBlur}
                        onChange={(event) => field.handleChange(event.target.value)}
                        placeholder="At least 8 characters"
                        required
                      />
                      {isInvalid ? <FieldError errors={field.state.meta.errors} /> : null}
                    </Field>
                  );
                }}
              </form.Field>
            </div>

            <div className="border-t px-4 pt-4">
              <form.Subscribe selector={(state) => [state.canSubmit, state.isSubmitting]}>
                {([canSubmit, isSubmitting]) => (
                  <Button className="w-full" type="submit" disabled={!canSubmit || isSubmitting}>
                    {isSubmitting ? <Loader2Icon className="size-4 animate-spin" /> : null}
                    Change password
                  </Button>
                )}
              </form.Subscribe>
            </div>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
}

function UserActionsCell({ user }: { user: User }) {
  const [sheetOpen, setSheetOpen] = React.useState(false);

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger
          aria-label="User actions"
          className="inline-flex size-8 items-center justify-center rounded-md hover:bg-accent"
        >
          <EllipsisIcon className="size-4" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={() => setSheetOpen(true)}>Change password</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <ChangePasswordSheet
        userId={user._id}
        userName={user.name ?? user.email}
        open={sheetOpen}
        onOpenChange={setSheetOpen}
      />
    </>
  );
}

function RouteComponent() {
  const confirm = useConfirmationDialog();

  const changeUserRole = useMutation(api.admin.changeUserRole).withOptimisticUpdate(
    (localStore, args) => {
      optimisticallyUpdateValueInPaginatedQuery(localStore, api.admin.listUsers, {}, (user) =>
        user._id === args.userId ? { ...user, role: args.newRole } : user,
      );
    },
  );
  const changeUserActiveStatus = useMutation(api.admin.changeUserActiveStatus).withOptimisticUpdate(
    (localStore, args) => {
      optimisticallyUpdateValueInPaginatedQuery(localStore, api.admin.listUsers, {}, (user) =>
        user._id === args.userId ? { ...user, isActive: args.isActive } : user,
      );
    },
  );

  const columns = React.useMemo<ColumnDef<StockFeatures, User>[]>(
    () => [
      {
        id: "select",
        header: ({ table }) => (
          <Checkbox
            checked={table.getIsAllPageRowsSelected()}
            onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
            aria-label="Select all"
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            aria-label={`Select row ${row.index + 1}`}
          />
        ),
        size: 32,
        enableSorting: false,
        enableHiding: false,
      },
      {
        id: "user",
        header: ({ column }) => <DataTableColumnHeader column={column} title="User" label="User" />,
        cell: ({ row }) => {
          const image = row.original.image ?? undefined;
          const name = row.original.name;
          const email = row.original.email;

          return (
            <div className="flex items-center gap-2">
              <Avatar>
                <AvatarImage src={image} alt={name} />
                <AvatarFallback>{getInitials(name)}</AvatarFallback>
              </Avatar>
              <div className="flex flex-col">
                <span className="font-medium">{name}</span>
                <span className="text-sm text-muted-foreground">{email}</span>
              </div>
            </div>
          );
        },
      },
      {
        id: "role",
        accessorKey: "role",
        header: ({ column }) => <DataTableColumnHeader column={column} title="Role" label="Role" />,
        cell: ({ row }) => {
          const role = row.original.role;

          const onChange = async (newRole: (typeof ROLES)[number]) => {
            const confirmed = await confirm({
              title: "Change User Role",
              description: <>Are you sure you want to change this user's role to {newRole}?</>,
              confirmText: "Change Role",
            });

            if (!confirmed) {
              return;
            }

            try {
              await changeUserRole({ userId: row.original._id, newRole });
            } catch (error) {
              if (error instanceof ConvexError) {
                if (error.data == "FORBIDDEN") {
                  toast.error("You do not have permission to change user roles.");
                }
              }
            }
          };

          return (
            <Select value={role} onValueChange={(val) => val && onChange(val)}>
              <SelectTrigger className="h-8 w-32">
                <SelectValue placeholder={role} className="capitalize" />
              </SelectTrigger>
              <SelectContent side="bottom">
                {ROLES.map((itemRole) => (
                  <SelectItem key={itemRole} value={itemRole} className="capitalize">
                    {itemRole}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          );
        },
      },
      {
        accessorKey: "emailVerified",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Email Verified" label="Email Verified" />
        ),
        cell: ({ row }) => <Checkbox checked={row.original.emailVerified} disabled />,
      },
      {
        id: "isActive",
        accessorKey: "isActive",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Active" label="Active" />
        ),
        cell: ({ row }) => {
          const isActive = row.original.isActive;

          const handleChange = React.useCallback(async (checked: boolean) => {
            if (checked === false) {
              const confirmed = await confirm({
                title: "Deactivate User",
                description: (
                  <>
                    Are you sure you want to deactivate this user? They will not be able to log in
                    or access any resources.
                  </>
                ),
                confirmText: "Deactivate",
                confirmButtonVariant: "destructive",
              });

              if (!confirmed) {
                return;
              }
            }

            try {
              await changeUserActiveStatus({ userId: row.original._id, isActive: checked });
            } catch (error) {
              if (error instanceof ConvexError) {
                if (error.data == "FORBIDDEN") {
                  toast.error("You do not have permission to change user active status.");
                }
              }
            }
          }, []);

          return <Checkbox checked={isActive} onCheckedChange={handleChange} />;
        },
      },
      {
        id: "createdAt",
        accessorKey: "createdAt",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Created At" label="Created At" />
        ),
        cell: ({ row }) => <RelativeTimeCard date={new Date(row.original.createdAt)} />,
      },
      {
        id: "updatedAt",
        accessorKey: "updatedAt",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Updated At" label="Updated At" />
        ),
        cell: ({ row }) => <RelativeTimeCard date={new Date(row.original.updatedAt)} />,
      },
      {
        id: "actions",
        header: () => <span className="sr-only">Actions</span>,
        cell: ({ row }) => <UserActionsCell user={row.original} />,
        size: 40,
        enableSorting: false,
        enableHiding: false,
      },
    ],
    [],
  );

  const { results, status, loadMore } = usePaginatedQuery(
    api.admin.listUsers,
    {},
    { initialNumItems: 40 },
  );

  const data = React.useMemo(() => results, [results]);
  const tableStatus = status === "LoadingFirstPage" ? "Exhausted" : status;

  const table = useDataTable({
    columns,
    data,
  });

  return (
    <DataTable
      table={table}
      status={tableStatus}
      loadMore={loadMore}
      actionBar={<TableActionBar table={table} />}
      className="p-4"
    >
      <DataTableToolbar table={table}>
        <CreateUserSheet />
      </DataTableToolbar>
    </DataTable>
  );
}
